'use client';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useEffectOnce } from '@utils/react/hooks';
import { getZones } from '@utils/graphql/creators/zones';
import {
  ActionModal,
  Button,
  Modal,
  Table,
} from '@skyline-platform/alchemy';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { parseSSoTEvent, SSOT_ACTIONS } from '@utils/eventHandler';
import '../split/page.css';
import { SearchBarComponent } from '@components/SearchBar/SearchBarComponent';
import { SkipToMainContent } from '@components/Misc';
import { ZoneActions } from './ZoneActions';
import { ZoneForm } from '@components/FormComponents/ZoneForm/ZoneForm';
import { buildSearchCriteria } from '../utils/helpers';
import {
  patchRow, removeRow, addRow,
} from '../split/utils/helpers';
import { getLogger } from '@utils/general';
import { useServerPagination } from '@utils/react/hooks/useServerPagination';
import PropTypes from 'prop-types';

const logger = getLogger('src.pages.zones.AllZones');

const ZONE_FETCH_FIELDS = [
  'id',
  'name',
  'description',
  'auto_offline_enabled',
  'last_updated',
];

const SEARCH_FIELD_MAP = {
  name: 'name',
  description: 'description',
  auto_offline_enabled: 'auto_offline_enabled',
  last_updated: 'last_updated',
};

const SORT_FIELD_MAP = {
  last_updated: 'last_updated',
};

// Fields rendered in the Zones table: name, description, auto_offline_enabled, last_updated.
// Update events where none of these change are dropped in handleEvent below.

export const AllZones = ({ userIsAdmin }) => {
  const searchOptions = [
    { key: 'Zone Name', value: 'name' },
    { key: 'Description', value: 'description' },
    { key: 'Transcode Auto-Offline', value: 'auto_offline_enabled' },
    { key: 'Last Updated', value: 'last_updated' },
  ];
  const [selectedFields, setSelectedFields] = useState(searchOptions.map((option) => option.value));

  const mapTableData = useCallback((zones) => {
    return zones.map((zone) => {
      const utcDate = new Date(zone.last_updated + ' UTC');
      const localDate = utcDate.toLocaleDateString();
      return {
        id: zone.id,
        name: zone.name,
        description: zone.description ?? '',
        auto_offline_enabled: zone.auto_offline_enabled ? 'True' : 'False',
        last_updated: localDate,
      };
    });
  }, []);

  const {
    rows,
    totalRows,
    loading,
    currentPage,
    rowsPerPage,
    handleSort,
    sortDirection,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    clearSearch,
    setRows,
    setTotalRows,
  } = useServerPagination({
    fetchFn: getZones,
    mapFn: mapTableData,
    fetchFields: ZONE_FETCH_FIELDS,
    defaultSort: { field: 'name', direction: 'asc' },
    sortFieldMap: SORT_FIELD_MAP,
    enableSort: true,
    onError: (error) => logger.error(error),
  });

  const hasActiveCriteriaRef = useRef(false);

  const handleServerSearch = useCallback((searchTerm, fields) => {
    if (!searchTerm) {
      if (hasActiveCriteriaRef.current) {
        hasActiveCriteriaRef.current = false;
        clearSearch();
      }
      return;
    }

    // Convert user-friendly date input to ISO format for last_updated.
    // 4/2      → 04-02
    // 4/2/2026 → 2026-04-02
    // This applies whenever last_updated is one of the selected fields.
    const convertDateTerm = (term) => {
      const parts = term.trim().split(/[/-]/);
      if (parts.length === 3) {
        // Full date: m/d/yyyy → yyyy-mm-dd (pad both month and day)
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      if (parts.length === 2) {
        // Partial date: m/d → mm-d (pad month only; day is a prefix so 4/2 matches 4/2, 4/21, 4/22...)
        return `${parts[0].padStart(2, '0')}-${parts[1]}`;
      }
      return term;
    };

    const hasLastUpdated = fields.includes('last_updated');
    const hasAutoOffline = fields.includes('auto_offline_enabled');
    const otherFields = fields.filter((f) => f !== 'last_updated' && f !== 'auto_offline_enabled');

    // Build criteria for non-date fields with the original term
    const regularCriteria = buildSearchCriteria(searchTerm, otherFields, SEARCH_FIELD_MAP);
    // Build criteria for last_updated separately with the converted term
    const dateCriteria = hasLastUpdated
      ? buildSearchCriteria(convertDateTerm(searchTerm), ['last_updated'], SEARCH_FIELD_MAP)
      : {};


    // For auto_offline_enabled, only filter if the input is a prefix of 'true' or 'false'.
    // If not, do not send a value for auto_offline_enabled at all (omit the key).
    let boolCriteria = {};
    if (hasAutoOffline) {
      const lower = searchTerm.trim().toLowerCase();
      if ('true'.startsWith(lower)) {
        boolCriteria.auto_offline_enabled = true;
      } else if ('false'.startsWith(lower)) {
        boolCriteria.auto_offline_enabled = false;
      }
    }

    // Merge: if multiple have results, combine into a single OR
    const allCriteria = [
      regularCriteria,
      dateCriteria,
      boolCriteria,
    ].filter((c) => Object.keys(c).length > 0);

    let criteria;
    if (allCriteria.length > 1) {
      const items = allCriteria.flatMap((c) => c.or ?? [c]);
      criteria = { or: items };
    } else if (allCriteria.length === 1) {
      [criteria] = allCriteria;
    } else {
      criteria = {};
    }
    // Always call the API if there is any search term
    hasActiveCriteriaRef.current = true;
    handleSearch(criteria);
  }, [handleSearch, clearSearch]);

  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const tableContext = {
    rowsRef,
    setRows,
    // removeRow calls setSelectedRowIds/setSelectedId to clean up selection state when a
    // row is deleted. AllZones has no row selection, so these are no-ops that satisfy the
    // tableContext interface without wiring up state that isn't used.
    setSelectedRowIds: () => { },
    setSelectedId: () => { },
  };

  /***** SUBSCRIPTIONS *****/
  // AllZones uses inline patchRow/removeRow/addRow rather than handleTableEvent because
  // Zone events only ever affect Zone rows — there is no fan-out to other model types.
  // handleTableEvent is the right tool when a single event (e.g. a zone rename) needs to
  // ripple into many rows of a different model (e.g. streams). That complexity is not needed
  // here, so the simpler inline approach is used instead.
  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    const handleEvent = async (eventMessage) => {
      const parsed = parseSSoTEvent(eventMessage, 'Zone');
      if (!parsed) return;
      const { action, data } = parsed;

      if (action === SSOT_ACTIONS.REMOVE) {
        if (data.previous?.id) {
          removeRow(data.previous.id, tableContext);
          setTotalRows((prev) => Math.max(0, prev - 1));
        }
        return;
      }

      if (!data.current?.id) return;

      if (action === SSOT_ACTIONS.CREATE) {
        const { query, execute } = getZones(data.current.id);
        query.addReturnProperties(...ZONE_FETCH_FIELDS);
        try {
          const { data: zoneData } = await execute();
          if (zoneData) {
            addRow(zoneData, mapTableData, tableContext);
            setTotalRows((prev) => prev + 1);
          }
        } catch (err) {
          logger.error(err);
        }
        return;
      }

      if (action === SSOT_ACTIONS.UPDATE) {
        const { current, previous } = data;
        const rowPatch = {};
        // Per-field blocks rather than a loop because last_updated requires a date
        // transform before it can be stored in the row. A uniform loop over field names
        // would need to special-case it anyway, so explicit blocks are clearer here.
        if (current.name !== previous?.name) rowPatch.name = current.name ?? '';
        if (current.description !== previous?.description) rowPatch.description = current.description ?? '';
        if (current.auto_offline_enabled !== previous?.auto_offline_enabled) {
          rowPatch.auto_offline_enabled = current.auto_offline_enabled ? 'True' : 'False';
        }
        if (current.last_updated !== previous?.last_updated) {
          rowPatch.last_updated = new Date(current.last_updated + ' UTC').toLocaleDateString();
        }
        if (Object.keys(rowPatch).length === 0) return;
        patchRow(current.id, rowPatch, tableContext);
      }
    };
    messageHandler.subscribe(handleEvent);
    return () => {
      messageHandler.unsubscribe(handleEvent);
    };
  });
  /***** END SUBSCRIPTIONS *****/

  const tableColumns = useMemo(() => {
    const cols = [
      {
        field: 'name',
        headerName: 'Zone Name',
        type: 'string',
        grow: 1,
        sortable: true,
      },
      {
        field: 'description',
        headerName: 'Description',
        type: 'string',
        grow: 1,
        sortable: true,
      },
      {
        field: 'auto_offline_enabled',
        headerName: 'Transcode Auto-Offline',
        type: 'string',
        grow: 1,
        sortable: true,
      },
      {
        field: 'last_updated',
        headerName: 'Last Updated',
        type: 'string',
        grow: 1,
        sortable: true,
      },
    ];

    if (userIsAdmin) {
      cols.push(
        {
          field: 'action',
          headerName: '',
          cell: (row) => (
            <ActionModal
              className='zone-row-actions'
              id={row.name}
              title='Toggle Options'
              content={ZoneActions({ rowInfo: row, id: row.id })}
            />
          ),
          width: '50px',
          sortable: false,
        });
    }

    return cols;
  }, [userIsAdmin]);

  return (
    <gar-all-zones>
      <div className='actions-bar'>
        <SearchBarComponent
          searchOptions={searchOptions}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          fieldName='Zones'
          onSearch={handleServerSearch}
        />
        {userIsAdmin && (
          <>
            <Modal
              className='new-zone-modal'
              closeIcon
              header='Create New Zone'
              trigger={({ handleOpen }) => (
                <Button
                  label='New Zone'
                  iconLeft='fa-solid fa-circle-plus'
                  state='default'
                  type='filled'
                  onClick={handleOpen}
                />
              )}
              slots={{
                content: (handleClose) => (
                  <ZoneForm
                    handleClose={handleClose}
                  />
                ),
              }}
            />
          </>
        )}
      </div>
      <SkipToMainContent
        text="Skip to pagination controls"
        targetSelector="alc-pagination-bar"
      />
      <div>
        <Table
          tableName='Zones'
          columns={tableColumns}
          hideInColumnMenu={['action']}
          rows={rows}
          initialSortField='name'
          defaultSortAsc={true}
          sortServer
          onSort={handleSort}
          paginationServer
          paginationTotalRows={totalRows}
          currentPage={currentPage}
          paginationPerPage={rowsPerPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          progressPending={loading}
          sortDirection={sortDirection}
          style={{ height: rows.length === 0 ? '400px' : undefined }}
        />
      </div>
    </gar-all-zones>
  );
};

AllZones.propTypes = {
  userIsAdmin: PropTypes.bool.isRequired,
};